// 인게임 상단 중앙(D1)에서 "N일 차 + 낮/밤"을 읽는다. OpenCV 없이 순수 JS로 작은 영역만 처리한다.
//
// 입력은 기준 해상도(높이 1440) 격자의 D1 영역(760×120 RGBA, 원본 좌표 x 1400~2160, y 0~120) 이미지다.
// 흰색 글자는 밝기 임계값으로 분리하고, 글자별로 잘라 작은 이진 이미지로 만들어 템플릿과 비교한다.
// (화면 해상도가 달라도 호출 쪽이 기준 격자로 줄이거나 늘려 넘겨 준다.)

export const CLOCK_REGION = { x: 1400, y: 0, width: 760, height: 120 }; // 기준 해상도(높이 1440)의 원본 좌표
export const CLOCK_CENTER_X = 1720; // 화면 가로 중앙(3440/2). HUD가 가로 중앙 기준으로 놓여 있어 해상도가 달라도 이 기준을 쓴다.

/**
 * 화면(video) 크기에서 D1 영역을 오려 올 원본 픽셀 위치. HUD는 가로 중앙 기준, 크기는 화면 높이 기준(높이 1440 = 배율 1)으로
 * 배치되므로 해상도/화면비가 달라도 같은 영역을 잡을 수 있다. 오려 온 영역은 CLOCK_REGION 크기(760×120)로 맞춰 읽는다.
 */
export function clockRegionRect(videoWidth, videoHeight) {
  const scale = videoHeight / 1440;
  return {
    sx: Math.round(videoWidth / 2 + (CLOCK_REGION.x - CLOCK_CENTER_X) * scale),
    sy: Math.round(CLOCK_REGION.y * scale),
    sw: Math.round(CLOCK_REGION.width * scale),
    sh: Math.round(CLOCK_REGION.height * scale),
  };
}

// D1 영역 안의 좌표
const DAY_BOX = { x: 164, y: 10, w: 50, h: 22 };
const ICON_BOX = { x: 184, y: 30, w: 40, h: 32 };

const WHITE_THRESHOLD = 185; // 글자(흰색)로 보는 밝기
const DAY_LABEL_THRESHOLD = 190; // 일차 라벨 글자(밝은 회색)는 타이머보다 조금 어둡다
const GLYPH_W = 10;
const GLYPH_H = 16;
const MAX_GLYPH_DISTANCE = 0.22; // classifyGlyph 기본 허용 거리(템플릿과 다른 픽셀 비율)
// 일차 글자는 샘플이 일차마다 1장뿐이라 허용 거리를 넓히고, 대신 2등과의 격차로 오인식을 막는다(숫자끼리는 0.4 이상 다르다).
const DAY_CLASSIFY = { maxDistance: 0.3, minMargin: 0.08 };
const ICON_MIN_PIXELS = 40; // 해(주황)/달(옅은 보라) 색 픽셀이 이만큼 보여야 판별한다
const MAX_SINGLE_GLYPH_WIDTH = 22; // 이보다 넓은 묶음은 글자 둘이 맞닿은 것으로 보고 가운데 골짜기에서 가른다

const luma = (data, i) => 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];

// 영역을 밝기 임계값으로 이진 마스크로 만든다(글자는 흰색~밝은 회색).
export function binarize(image, box, threshold = WHITE_THRESHOLD) {
  const mask = new Uint8Array(box.w * box.h);
  for (let y = 0; y < box.h; y += 1) {
    for (let x = 0; x < box.w; x += 1) {
      mask[y * box.w + x] = luma(image.data, ((box.y + y) * image.width + (box.x + x)) * 4) >= threshold ? 1 : 0;
    }
  }
  return mask;
}

// 세로로 한 줄이라도 흰 픽셀이 있는 연속 구간을 글자 후보로 묶는다. 각 후보는 잉크 영역의 bbox다.
export function segmentGlyphs(mask, width, height) {
  const columnInk = Array.from({ length: width }, (_, x) => {
    let count = 0;
    for (let y = 0; y < height; y += 1) {
      count += mask[y * width + x];
    }
    return count;
  });
  const groups = [];
  let start = null;
  for (let x = 0; x <= width; x += 1) {
    const inked = x < width && columnInk[x] > 0;
    if (inked && start === null) {
      start = x;
    } else if (!inked && start !== null) {
      groups.push({ x0: start, x1: x - 1 });
      start = null;
    }
  }
  // 글자 둘이 맞닿아 한 묶음이 된 경우(예: 2 다음에 4): 가운데 쪽에서 잉크가 가장 적은 열에서 가른다.
  const split = groups.flatMap((g) => {
    if (g.x1 - g.x0 + 1 <= MAX_SINGLE_GLYPH_WIDTH) {
      return [g];
    }
    const from = g.x0 + Math.floor((g.x1 - g.x0) * 0.3);
    const to = g.x0 + Math.ceil((g.x1 - g.x0) * 0.7);
    let valley = from;
    for (let x = from; x <= to; x += 1) {
      if (columnInk[x] < columnInk[valley]) {
        valley = x;
      }
    }
    return [{ x0: g.x0, x1: valley - 1 }, { x0: valley + 1, x1: g.x1 }];
  });
  return split
    .map((g) => {
      let y0 = height;
      let y1 = -1;
      let ink = 0;
      for (let y = 0; y < height; y += 1) {
        for (let x = g.x0; x <= g.x1; x += 1) {
          if (mask[y * width + x]) {
            y0 = Math.min(y0, y);
            y1 = Math.max(y1, y);
            ink += 1;
          }
        }
      }
      return { ...g, y0, y1, ink };
    })
    .filter((g) => g.ink >= 4); // 잡티 제거
}

// 글자 bbox를 GLYPH_W×GLYPH_H 이진 이미지('0'/'1' 문자열)로 줄인다(최근접).
export function glyphToBits(mask, width, g) {
  const w = g.x1 - g.x0 + 1;
  const h = g.y1 - g.y0 + 1;
  let bits = '';
  for (let oy = 0; oy < GLYPH_H; oy += 1) {
    for (let ox = 0; ox < GLYPH_W; ox += 1) {
      const sx = g.x0 + Math.min(w - 1, Math.floor(((ox + 0.5) * w) / GLYPH_W));
      const sy = g.y0 + Math.min(h - 1, Math.floor(((oy + 0.5) * h) / GLYPH_H));
      bits += mask[sy * width + sx] ? '1' : '0';
    }
  }
  return bits;
}

const hamming = (a, b) => {
  let diff = 0;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) {
      diff += 1;
    }
  }
  return diff / a.length;
};

// 가장 비슷한 템플릿 값과 거리. 거리가 maxDistance를 넘거나 2등과의 격차(minMargin)가 작으면 value는 null이다.
export function classifyGlyph(bits, templates, { maxDistance = MAX_GLYPH_DISTANCE, minMargin = 0 } = {}) {
  let best = { value: null, distance: 1 };
  let secondDistance = 1;
  Object.entries(templates).forEach(([value, template]) => {
    const distance = hamming(bits, template);
    if (distance < best.distance) {
      secondDistance = best.distance;
      best = { value, distance };
    } else if (distance < secondDistance) {
      secondDistance = distance;
    }
  });
  const confident = best.distance <= maxDistance && secondDistance - best.distance >= minMargin;
  return confident ? best : { value: null, distance: best.distance };
}

// 일차 숫자 글자(라벨 "N일 차"의 첫 글자)
export function readDayGlyph(image) {
  const mask = binarize(image, DAY_BOX, DAY_LABEL_THRESHOLD);
  const groups = segmentGlyphs(mask, DAY_BOX.w, DAY_BOX.h);
  return groups.length ? glyphToBits(mask, DAY_BOX.w, groups[0]) : null;
}

// 아이콘 색으로 낮/밤을 구분한다. 달은 옅은 보라(파랑 성분이 크고 초록보다 높음), 해는 주황이다.
// 어느 쪽도 충분히 안 보이면(색이 번졌거나 가려짐) 단정하지 않고 null로 돌려준다.
export function readPhase(image) {
  let moonPixels = 0;
  let sunPixels = 0;
  for (let y = 0; y < ICON_BOX.h; y += 1) {
    for (let x = 0; x < ICON_BOX.w; x += 1) {
      const i = ((ICON_BOX.y + y) * image.width + (ICON_BOX.x + x)) * 4;
      const r = image.data[i];
      const g = image.data[i + 1];
      const bl = image.data[i + 2];
      if (bl >= 180 && bl - g >= 20 && r >= 120) {
        moonPixels += 1;
      } else if (r >= 200 && g >= 110 && g <= 215 && bl <= 110) {
        sunPixels += 1;
      }
    }
  }
  if (moonPixels >= ICON_MIN_PIXELS && moonPixels > sunPixels) {
    return 'night';
  }
  return sunPixels >= ICON_MIN_PIXELS ? 'day' : null;
}

/**
 * D1 영역 이미지 → { day, phase }. 낮/밤 아이콘을 못 읽으면 null, 일차 숫자만 못 읽으면 day: null이다.
 * (타이머 숫자는 읽지 않는다. 데미지 숫자 등이 타이머를 가려도 일차/낮밤은 읽힌다.)
 */
export function readClock(image, templates) {
  const phase = readPhase(image);
  if (phase === null) {
    return null;
  }
  const dayBits = readDayGlyph(image);
  const day = dayBits ? classifyGlyph(dayBits, templates.day, DAY_CLASSIFY).value : null;
  return { day: day === null ? null : Number(day), phase };
}
